import { BUILDING_PROPERTIES } from '@/interfaces/building.interface'
import { COMPLEX_PROPERTIES } from '@/interfaces/complex.interface'
import { LEVEL_PROPERTIES } from '@/interfaces/level.interface'
import { PoiProperties } from '@/interfaces/poi.interface'
import { PoiInterface as navVisPoi } from '@navvis/ivion'
import { REAL_ESTATE_PROPERTIES } from '@/interfaces/realEstate.interface'
import { Room, ROOM_PROPERTIES } from '@/interfaces/room.interface'
import { FeatureCollection, Geometry } from '@turf/turf'
import { ZoneProperties } from '@/services/zones.service'
import { LineGeoJsonProperties } from '@/services/lines.service'

export enum BreadcrumbType {
  COMPLEX = 'complex',
  REAL_ESTATE = 'realEstate',
  BUILDING = 'building',
  LEVEL = 'level',
  ROOM = 'room',
  POI = 'poi',
  ZONE = 'zone',
  LINE = 'line',
  SEVERAL_COMPLEXES = 'several-complexes',
  SEVERAL_REAL_ESTATES = 'several-real-estates',
  SEVERAL_BUILDINGS = 'several-buildings'
}

export type BreadcrumbOption = {
  id?: string
  title: string
  tooltipText?: string
  type?: BreadcrumbType
  icon?: string
}

export type LevelRooms = {
  data: FeatureCollection<Geometry, ROOM_PROPERTIES>
}

export type BreadcrumbArgs = {
  t: (key: string) => string
  realm: string | null
  selectedComplex: COMPLEX_PROPERTIES | undefined
  selectedRealEstate: REAL_ESTATE_PROPERTIES | undefined
  selectedBuildings: BUILDING_PROPERTIES[] | undefined
  selectedLevel: LEVEL_PROPERTIES | undefined
  selectedLine: LineGeoJsonProperties | null
  selectedRoom: Room | undefined
  selectedPoi: PoiProperties | undefined
  selectedZone: ZoneProperties | null
  roomsForSelectedLevel: LevelRooms
  isBuildingSelected: boolean
}

export const generateBreadcrumbOptions = ({
  t,
  realm,
  selectedComplex,
  selectedRealEstate,
  selectedBuildings,
  selectedLevel,
  selectedLine,
  selectedRoom,
  selectedPoi,
  selectedZone,
  roomsForSelectedLevel,
  isBuildingSelected
}: BreadcrumbArgs): BreadcrumbOption[] =>
  [
    addComplexBreadcrumbOptions(t, realm, selectedComplex),
    addRealEstateBreadcrumbOptions(t, selectedRealEstate),
    addPoiRelatedBreadcrumbOptions(
      t,
      roomsForSelectedLevel,
      selectedPoi,
      isBuildingSelected,
      'realEstate'
    ),
    addLineRelatedBreadcrumbOptions(t, selectedLine),
    addZoneRelatedBreadcrumbOptions(t, selectedZone, selectedLevel, 'realEstate'),
    addBuildingBreadcrumbOptions(t, selectedBuildings ?? []),
    addLevelBreadcrumbOptions(t, selectedLevel),
    addZoneRelatedBreadcrumbOptions(t, selectedZone, selectedLevel, 'level'),
    addPoiRelatedBreadcrumbOptions(
      t,
      roomsForSelectedLevel,
      selectedPoi,
      isBuildingSelected,
      'level',
      selectedLevel
    ),
    addRoomBreadcrumbOptions(t, selectedRoom),
    addPoiRelatedBreadcrumbOptions(t, roomsForSelectedLevel, selectedPoi, isBuildingSelected)
  ].reduce(
    (acc: Options, generator) => {
      // Terminate if stop is true
      if (acc.stop) return acc
      // Otherwise, generate next options
      const { options: newOptions, stop } = generator()
      // Accumulate options
      return {
        options: [...acc.options, ...newOptions],
        stop
      }
    },
    {
      options: [],
      stop: false
    }
  ).options

type Options = {
  options: BreadcrumbOption[]
  stop: boolean
}

// Single option generator
const singleOption = (option: BreadcrumbOption, stop = false): Options => ({
  options: [option],
  stop
})

// Single option generator with optional value unwrapping
const singleOptionUnwrap = <T>(
  value: T | undefined,
  option: (value: T) => BreadcrumbOption,
  stop = false
): Options => (value ? singleOption(option(value), stop) : { options: [], stop })

const addComplexBreadcrumbOptions =
  (
    t: (key: string) => string,
    realm: string | null,
    selectedComplex: COMPLEX_PROPERTIES | undefined
  ) =>
  (): Options =>
    singleOptionUnwrap(selectedComplex, (selectedComplex) => ({
      id: selectedComplex.belongsToId,
      title: selectedComplex.title, // displayName handled in complex service file
      tooltipText:
        realm === 'akademiskahus'
          ? `${t('general__text__show')} ${t('title__complex__campus').toLowerCase()} ${t(
              'general__text__in'
            ).toLowerCase()} ${t('general__view__mapview').toLowerCase()}`
          : `${t('general__text__show')} ${t('general__title__complex').toLowerCase()} ${t(
              'general__text__in'
            ).toLowerCase()} ${t('general__view__mapview').toLowerCase()}`,
      type: BreadcrumbType.COMPLEX
    }))

const addRealEstateBreadcrumbOptions =
  (t: (key: string) => string, selectedRealEstate: REAL_ESTATE_PROPERTIES | undefined) =>
  (): Options =>
    singleOptionUnwrap(selectedRealEstate, (selectedRealEstate) => ({
      id: selectedRealEstate.id,
      title: selectedRealEstate.displayName,
      tooltipText: `${t('general__text__show')} ${t(
        'general__title__realestate'
      ).toLowerCase()} ${t('general__text__in').toLowerCase()} ${t(
        'general__view__mapview'
      ).toLowerCase()}`,
      type: BreadcrumbType.REAL_ESTATE
    }))

const addBuildingBreadcrumbOptions =
  (t: (key: string) => string, selectedBuildings: BUILDING_PROPERTIES[]) => (): Options => {
    const uniqueBuildings = Array.from(
      new Set(
        (selectedBuildings ?? [])
          .filter((building) => building !== undefined && building.id !== undefined)
          .map((building) => building.id)
      ).values()
    )
      .map((id) => selectedBuildings?.find((building) => building.id === id))
      .filter((building) => building !== undefined) as BUILDING_PROPERTIES[]
    if (uniqueBuildings.length > 1) {
      // If there are several buildings, show a "Several buildings" option
      return singleOption(
        {
          title: t('general__data__several-buildings'),
          type: BreadcrumbType.SEVERAL_BUILDINGS
        },
        true
      )
    } else if (uniqueBuildings.length === 1) {
      // If there is only one building, return it
      const selectedBuilding = uniqueBuildings[0]
      return singleOption({
        id: selectedBuilding.id,
        title: selectedBuilding.displayName,
        tooltipText: `${t('general__text__show')} ${t('general__text__building').toLowerCase()} ${t(
          'general__text__in'
        ).toLowerCase()} ${t('general__view__mapview').toLowerCase()}`,
        type: BreadcrumbType.BUILDING
      })
    }
    // If there are no buildings, return empty options and stop
    return { options: [], stop: true }
  }

const addLevelBreadcrumbOptions =
  (t: (key: string) => string, selectedLevel: LEVEL_PROPERTIES | undefined) => (): Options =>
    singleOptionUnwrap(selectedLevel, (selectedLevel) => ({
      id: selectedLevel.id,
      title: `${t('general__text__level')} ${selectedLevel.level}`,
      tooltipText: `${t('general__text__show')} ${t('general__text__floor').toLowerCase()} ${t(
        'general__text__in'
      ).toLowerCase()} ${t('general__view__planview').toLowerCase()}`,
      type: BreadcrumbType.LEVEL
    }))

const addRoomBreadcrumbOptions =
  (t: (key: string) => string, selectedRoom: Room | undefined) => (): Options =>
    singleOptionUnwrap(selectedRoom, (selectedRoom) => ({
      id: selectedRoom.id,
      title: selectedRoom.properties.displayName,
      tooltipText: `${t('general__text__show')} ${t('general__text__room').toLowerCase()} ${t(
        'general__text__in'
      ).toLowerCase()} ${t('general__view__planview').toLowerCase()}`,
      type: BreadcrumbType.ROOM
    }))

function findRoomForPoi(
  roomsForSelectedLevel: LevelRooms,
  selectedPoi: PoiProperties
): Room | undefined {
  return roomsForSelectedLevel.data.features.find((room) => room.id === selectedPoi.coreId) as
    | Room
    | undefined
}

function poiTitle(isPoiFromBackend: boolean, selectedPoi: PoiProperties): string {
  if (!isPoiFromBackend) return selectedPoi.title ?? ''
  return selectedPoi.name ? selectedPoi.name : selectedPoi.title ?? ''
}

const addPoiRelatedBreadcrumbOptions =
  (
    t: (key: string) => string,
    roomsForSelectedLevel: LevelRooms,
    selectedPoi: PoiProperties | undefined,
    isBuildingSelected: boolean,
    attachedToCoreType?: 'realEstate' | 'building' | 'level' | 'room',
    selectedLevel?: LEVEL_PROPERTIES | undefined
  ) =>
  (): Options => {
    if (!selectedPoi) return { options: [], stop: false }
    if (attachedToCoreType === 'realEstate' && isBuildingSelected)
      return { options: [], stop: false }
    const options: BreadcrumbOption[] = []
    const isPoiFromBackend =
      !(selectedPoi as unknown as navVisPoi)?.matching_title &&
      !(selectedPoi as unknown as navVisPoi)?.pov

    // If the POI is from the backend, we need to find the room it belongs to and add it to the breadcrumb
    if (isPoiFromBackend && isBuildingSelected) {
      const roomWithPoi = findRoomForPoi(roomsForSelectedLevel, selectedPoi)
      if (attachedToCoreType === 'level' && selectedLevel) {
        if (selectedPoi.coreId !== selectedLevel.id) return { options: [], stop: false }
      } else if (roomWithPoi) {
        options.push({
          id: roomWithPoi.id?.toString(),
          title: roomWithPoi.properties.displayName,
          tooltipText: `${t('general__text__show')} ${t('general__text__room').toLowerCase()} ${t(
            'general__text__in'
          ).toLowerCase()} ${t('general__view__planview').toLowerCase()}`,
          type: BreadcrumbType.ROOM
        })
      } else {
        // If the POI is from the backend but we can't find the room it belongs to
        return { options: [], stop: false }
      }
    }

    options.push({
      id: selectedPoi.guid,
      title: poiTitle(isPoiFromBackend, selectedPoi),
      tooltipText: `${t('general__text__show')} ${t('general__text__poi').toLowerCase()} ${t(
        'general__text__in'
      ).toLowerCase()} ${t('general__view__planview').toLowerCase()}`,
      type: BreadcrumbType.POI
    })
    if (!isBuildingSelected) return { options, stop: true }
    return { options, stop: false }
  }

const addZoneRelatedBreadcrumbOptions =
  (
    t: (key: string) => string,
    selectedZone: ZoneProperties | null,
    selectedLevel: LEVEL_PROPERTIES | undefined,
    attachedToCoreType?: 'realEstate' | 'level'
  ) =>
  (): Options => {
    if (!selectedZone) return { options: [], stop: false }
    if (attachedToCoreType === 'realEstate' && selectedLevel) return { options: [], stop: false }
    return singleOptionUnwrap(selectedZone, (selectedZone) => ({
      id: selectedZone.id,
      title: selectedZone.name,
      type: BreadcrumbType.ZONE
    }))
  }

const addLineRelatedBreadcrumbOptions =
  (t: (key: string) => string, selectedLine: LineGeoJsonProperties | null) => (): Options => {
    if (!selectedLine) return { options: [], stop: false }
    // handle line similar to zone when enabled for level as well
    return singleOptionUnwrap(selectedLine, (selectedLine) => ({
      id: selectedLine.id,
      title: selectedLine.name,
      type: BreadcrumbType.LINE
    }))
  }
